import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@/contexts/UserContext";

interface ModuleAccess {
  isEnabled: (moduleName: string) => boolean;
  isLoading: boolean;
  moduleSettings: Map<string, boolean> | undefined;
}

// Map of module names to their corresponding features
const MODULE_FEATURE_MAP: Record<string, string[]> = {
  sentinel: ["sentinel"],
  facility_presence: ["facility_presence", "facilityPresence"],
  scheduling: ["scheduling", "mySchedule", "My Teams Schedule", "scheduleAlerts", "scheduleLogs"],
  reports: ["reports", "reportsSubmissions"],
  hr_management: ["hr", "hrManagement"],
  missions: ["missions", "workInstructions"],
  performance: ["performance", "performanceEvaluation"],
  training: ["training", "trainingManagement"],
  whistleblowers: ["whistleblowers"],
  provider_evaluations: ["providerEvaluations", "provider-evaluations"],
  finance: ["finance", "hourlyCosts", "hourly-costs"],
  ethics_compliance: ["ethics_compliance", "ethicsCompliance", "ethics-compliance"],
  equipment_management: ["equipment_management", "equipmentManagement", "equipment"],
  supplier_evaluations: ["supplier_evaluations", "supplierEvaluations", "supplier-evaluations"],
};

export const useModuleAccess = (): ModuleAccess => {
  const { user, isAdmin } = useUser();

  const { data: moduleSettings, isLoading } = useQuery({
    queryKey: ["userModuleAccess", user?.id],
    queryFn: async () => {
      if (!user?.id) {
        console.log("🔒 No user ID, returning empty Map");
        return new Map<string, boolean>();
      }

      // Get the admin ID (either the user themselves if admin, or their creator)
      let adminId = user.id;
      
      if (!isAdmin) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("created_by")
          .eq("id", user.id)
          .single();
        
        console.log("👤 User profile for module access:", profile);
        
        if (profile?.created_by) {
          adminId = profile.created_by;
        }
      }

      console.log("🔍 Fetching module settings for admin:", adminId);

      const { data, error } = await supabase
        .from("admin_module_settings")
        .select("module_name, is_enabled")
        .eq("admin_id", adminId);

      if (error) {
        console.error("❌ Error fetching module settings:", error);
        return new Map<string, boolean>();
      }

      console.log("📦 Module settings data:", data);

      // Create a map of module states (default to enabled if not set)
      const settingsMap = new Map(data?.map(setting => [setting.module_name, setting.is_enabled]) || []);
      console.log("🗺️ Module settings Map:", Array.from(settingsMap.entries()));
      
      return settingsMap;
    },
    enabled: !!user?.id,
  });

  const isEnabled = (featureName: string): boolean => {
    // If loading or no settings, default to enabled
    if (isLoading || !moduleSettings) {
      console.log(`🔓 Module "${featureName}" enabled by default (loading: ${isLoading}, has settings: ${!!moduleSettings})`);
      return true;
    }

    // Find which module this feature belongs to
    for (const [moduleName, features] of Object.entries(MODULE_FEATURE_MAP)) {
      if (features.includes(featureName)) {
        const isModuleEnabled = moduleSettings.has(moduleName) ? moduleSettings.get(moduleName)! : true;
        console.log(`🔍 Feature "${featureName}" mapped to module "${moduleName}": ${isModuleEnabled ? '✅ enabled' : '❌ disabled'}`);
        return isModuleEnabled;
      }
    }

    // If feature not mapped to any module, allow by default
    console.log(`⚠️ Feature "${featureName}" not mapped to any module, allowing by default`);
    return true;
  };

  return {
    isEnabled,
    isLoading,
    moduleSettings, // Export the actual settings so components can track changes
  };
};
